import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { apiError } from "../lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "jb_token";
const DEVICE_KEY = "unwind_device_id";

function getOrCreateDeviceId() {
  let did = localStorage.getItem(DEVICE_KEY);
  if (!did) {
    // Random 128-bit id, base36-encoded. Not a security boundary on its own —
    // it just lets the backend recognise "same browser after successful OTP".
    const bytes = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    did = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
    localStorage.setItem(DEVICE_KEY, did);
  }
  return did;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setMustChangePassword(false);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        setMustChangePassword(!!r.data?.must_change_password);
      })
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, []);

  // Step 1: password login. Returns one of:
  //   { otp_required: true, otp_session, masked_email, expires_in_minutes }
  //   { token, user, must_change_password? }  (trusted device / OTP disabled)
  const login = async (email, password) => {
    const device_id = getOrCreateDeviceId();
    const { data } = await api.post("/auth/login", { email, password, device_id });
    if (data.otp_required) return data;
    return _consumeLoginPayload(data);
  };

  // Step 2 (only when otp_required): submit the emailed 6-digit code.
  const verifyOtp = async (otp_session, code, trust_device = false) => {
    const device_id = getOrCreateDeviceId();
    const { data } = await api.post("/auth/verify-otp", {
      otp_session, code, device_id, trust_device,
    });
    return _consumeLoginPayload(data);
  };

  const resendOtp = async (otp_session) => {
    const { data } = await api.post("/auth/resend-otp", { otp_session });
    return data;
  };

  // Forced first-login change: no current_password. Regular reset: with it.
  const changePassword = async ({ current_password, new_password }) => {
    const body = { new_password };
    if (current_password) body.current_password = current_password;
    const { data } = await api.post("/auth/change-password", body);
    return _consumeLoginPayload(data);
  };

  function _consumeLoginPayload(data) {
    if (data?.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      setMustChangePassword(!!data.must_change_password || !!data.user?.must_change_password);
      return data.user;
    }
    return data;
  }

  const logout = () => {
    clearSession();
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider
      value={{
        user, loading, mustChangePassword,
        login, verifyOtp, resendOtp, changePassword, logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { apiError };
